import { FC, ReactNode } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classNames from 'classnames';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';
import TranslationToggle from 'dibs-elements/exports/TranslationToggle';
import {
    useItemTranslations,
    INTERFACE_STATE,
} from '../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider';
import { trackTranslationToggle } from '../tracking/sharedTranslationToggleTracking';
import { moreDetails, seeLess } from '../pdpMessages';
import { PdpSharedDescriptionAbout_item$data } from './__generated__/PdpSharedDescriptionAbout_item.graphql';

// styles
import styles from './PdpSharedDescription.scss';

export const descriptionTitle = (
    <FormattedMessage id="PdpSharedDescriptionAbout.aboutHeader" defaultMessage="About" />
);

type Props = {
    wrapperClass?: string;
    headerClass?: string;
    contentClass?: string;
    item: PdpSharedDescriptionAbout_item$data;
    collapsedHeight?: number;
    lineClamp?: number;
    showExpandingArea?: boolean;
    title?: ReactNode;
};

type ContentProps = Pick<
    Props,
    'contentClass' | 'collapsedHeight' | 'lineClamp' | 'showExpandingArea'
> & {
    description?: string | null;
    englishDescription?: string | null;
    showOriginal: boolean;
};

export const Content: FC<ContentProps> = ({
    description,
    englishDescription,
    contentClass,
    collapsedHeight,
    lineClamp,
    showExpandingArea = true,
    showOriginal,
}) => {
    if (!description) {
        return null;
    }
    const descriptionToShow = showOriginal ? englishDescription : description;

    const content = (
        <span
            className={classNames(styles.content, contentClass)}
            data-tn="pdp-item-description-content"
            dangerouslySetInnerHTML={{ __html: (descriptionToShow || '').trim() }}
        />
    );
    return (
        <>
            {showExpandingArea ? (
                <ExpandingAreaWithReadMore
                    readMore={moreDetails}
                    readLess={seeLess}
                    collapsedHeight={collapsedHeight || 192}
                    lineClamp={lineClamp}
                    showGradient={false}
                    wrapperClass={styles.expandingAreaWrapper}
                >
                    {content}
                </ExpandingAreaWithReadMore>
            ) : (
                content
            )}
        </>
    );
};

const PdpSharedDescriptionAboutComponent: FC<Props> = ({
    item,
    contentClass,
    headerClass,
    collapsedHeight,
    lineClamp,
    showExpandingArea,
    title = descriptionTitle,
}) => {
    const [state, dispatch] = useItemTranslations();
    if (!item || !item.seoDescription) {
        return null;
    }

    return (
        <div data-tn="pdp-item-description" className={styles.wrapper}>
            <div className={styles.headerContainer}>
                <h2
                    className={classNames(styles.header, headerClass)}
                    data-tn="pdp-item-description-title"
                >
                    {title}
                </h2>
                {state && dispatch && (
                    <TranslationToggle
                        className={styles.translationToggleContainer}
                        currentState={state.interfaceState}
                        onClick={() => {
                            trackTranslationToggle();
                            dispatch({ type: 'TOGGLE_TRANSLATIONS' });
                        }}
                    />
                )}
            </div>
            <Content
                description={item.seoDescription}
                englishDescription={state?.data?.viewer?.item?.seoDescription}
                showOriginal={state?.interfaceState === INTERFACE_STATE.SHOW_ORIGINAL}
                contentClass={contentClass}
                collapsedHeight={collapsedHeight}
                lineClamp={lineClamp}
                showExpandingArea={showExpandingArea}
            />
        </div>
    );
};

export const PdpSharedDescriptionAbout = createFragmentContainer(
    PdpSharedDescriptionAboutComponent,
    {
        item: graphql`
            fragment PdpSharedDescriptionAbout_item on Item {
                seoDescription
            }
        `,
    }
);
